/**
 * Delivery performance for a whole purchase order — On Time / Early / Late
 * across every GRN of the order.
 *
 * Customer review 11 Sep (item 26c): the Lines panel of the Purchase Order
 * Details modal gets the same donut the Delivery Details modal shows for one
 * PO line (GRNScoreChart), at order grain. Counts come from the order's
 * insights payload; clicking opens the Deliveries tab filtered to this PO.
 */
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer } from 'recharts';
import type { PODeliveryPerformance } from '../../lib/api';

interface PODeliveryPerformanceChartProps {
  poNumber: string;
  performance: PODeliveryPerformance;
}

// Same palette and labels as GRNScoreChart, so the two charts read alike.
const SLICES: { key: keyof Omit<PODeliveryPerformance, 'total'>; label: string; color: string }[] = [
  { key: 'on_time', label: 'On Time', color: '#22c55e' },
  { key: 'early', label: 'Early', color: '#3b82f6' },
  { key: 'late', label: 'Late', color: '#ef4444' },
  { key: 'unknown', label: 'Unknown', color: '#9ca3af' },
];

export default function PODeliveryPerformanceChart({ poNumber, performance }: PODeliveryPerformanceChartProps) {
  const total = performance.total;
  const href = `/purchasing?tab=delivery&dlv_po=${encodeURIComponent(poNumber)}`;

  if (total === 0) {
    return (
      <div className="border rounded-lg p-4">
        <h4 className="text-sm font-semibold text-gray-700 mb-1">Delivery Performance (PO)</h4>
        <p className="text-sm text-muted-foreground">No deliveries received against this order yet.</p>
      </div>
    );
  }

  const data = SLICES
    .map((s) => ({ name: s.label, value: performance[s.key], color: s.color }))
    .filter((d) => d.value > 0);

  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="block border rounded-lg p-4 hover:bg-gray-50 transition-colors"
      title="Open this order's deliveries"
    >
      <h4 className="text-sm font-semibold text-gray-700 mb-2">Delivery Performance (PO)</h4>
      <div className="flex items-center gap-4">
        <ResponsiveContainer width={120} height={120}>
          <PieChart>
            <Pie data={data} cx="50%" cy="50%" innerRadius={30} outerRadius={50} dataKey="value" strokeWidth={1}>
              {data.map((entry) => (
                <Cell key={entry.name} fill={entry.color} />
              ))}
            </Pie>
            <Tooltip formatter={(value: number) => [`${value} / ${total}`, '']} />
          </PieChart>
        </ResponsiveContainer>
        <div className="space-y-1 text-xs">
          {data.map((entry) => (
            <div key={entry.name} className="flex items-center gap-2">
              <span className="w-3 h-3 rounded-full inline-block" style={{ backgroundColor: entry.color }} />
              <span>{entry.name}: {entry.value} ({Math.round((entry.value / total) * 100)}%)</span>
            </div>
          ))}
          <div className="text-gray-400 mt-1">
            Total: {total.toLocaleString()} {total === 1 ? 'delivery' : 'deliveries'} (click for details)
          </div>
        </div>
      </div>
    </a>
  );
}
